// noinspection DuplicatedCode
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import PartnerProfileService from 'api/PartnerProfileService';

const useFileBlockEditor = (props) => {
  const { contentBlock, onCancel, partnerId, onSaveSuccessfully } = props;
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: contentBlock,
  });
  const [processTypeLabel, setProcessTypeLabel] = useState('Add');
  const [isAddFile, setIsAddFile] = useState(false);
  const [isEditFile, setIsEditFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const { isLoading, refetch } = useQuery(
    'filesContentBlockInstance',
    () => PartnerProfileService.getContentBlock(getValues().id),
    {
      enabled: !!contentBlock.id,
      onSuccess: (data) => {
        if (data.data) {
          let mappedData = data?.data?.data.map((x) => ({
            id: x.id,
            dataKey: x.dataKey,
            displayOrder: x.value?.displayOrder,
            title: x.value?.title,
            description: x.value?.description,
            mediaPath: x.value?.mediaPath,
          }));
          if (deletedFiles.length > 0) {
            deletedFiles.forEach((id) => {
              mappedData = mappedData.filter((m) => m.id !== id);
            });
          }
          setFileList(mappedData);
          setValue('id', data?.data?.id);
          setValue('dataKey', data?.data?.dataKey);
          setValue('title', data?.data?.title);
          setValue('description', data?.data?.description);
          setProcessTypeLabel('Edit');
        }
        setIsFetching(false);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => PartnerProfileService.deleteBlockItem(id),
    {
      onSuccess: () => {
        onSaveSuccessfully({});
      },
    }
  );

  const mutationSaveBlock = useMutation(
    (payload) => PartnerProfileService.addBlock(payload),
    {
      onSuccess: ({ data }) => {
        onSaveSuccessfully();
        if (data) {
          setValue('id', data?.id);
        }
        setTimeout(() => onCancel(), 300);
      },
    }
  );

  const mutationDeleteBlockData = useMutation(
    (blockDataId) => PartnerProfileService.deleteBlockData(blockDataId),
    {
      onSuccess: () => {
        setTimeout(() => refetch(), 500);
      },
    }
  );

  const mutationUpdateBlock = useMutation(
    (payload) => PartnerProfileService.updateBlock(payload),
    {
      onSuccess: () => {
        onSaveSuccessfully();
        setTimeout(() => onCancel(), 300);
        refetch();
      },
    }
  );

  const isProcessing = () =>
    isLoading ||
    mutationUpdateBlock.isLoading ||
    mutationSaveBlock.isLoading ||
    isFetching;

  const onAddFile = () => {
    setIsAddFile(true);
  };
  const onFileFormCancel = () => {
    setIsAddFile(false);
    setIsEditFile(false);
  };
  const editFile = (file) => {
    setIsEditFile(true);
    setSelectedFile(file);
  };

  const insertBlockData = (blockInstanceId, blockDataValue, files) => {
    setIsFetching(true);
    PartnerProfileService.addBlockData(blockInstanceId, {
      dataKey: 'FILES_',
      value: JSON.stringify(blockDataValue),
      groupKey: 'Files',
    }).then(({ data }) => {
      const complete = () => {
        refetch();
        onSaveSuccessfully();
        setTimeout(() => {
          onFileFormCancel();
        }, 1000);
      };
      if (data && data.dataKey) {
        PartnerProfileService.uploadFile({
          file: files,
          blockDataItemDataKey: data.dataKey,
        })
          .then(() => {
            complete();
          })
          .catch(() => {
            complete();
          });
      }
    });
  };
  const onAddFileFormUpload = (file) => {
    const temp = file;
    temp.displayOrder = Number(temp.displayOrder);
    if (fileList.length === 0 && temp.displayOrder === 0) {
      temp.displayOrder = 1;
    } else {
      temp.displayOrder =
        temp.displayOrder === 0 ? fileList.length + 1 : temp.displayOrder;
    }
    if (fileList.length === 0 && getValues().id.length <= 0) {
      const formData = getValues();
      const block = {
        id: formData.id,
        partnerId,
        contentType: 'files',
        title: formData.title,
        description: formData.description,
        displayOrder: 1,
      };
      // we need to make sure that we save the block first then use the id of that to set the data
      PartnerProfileService.addBlock(block).then(({ data }) => {
        if (data) {
          setValue('id', data?.id);
        }
        const payload = {
          ...temp,
          mediaPath: '',
          mediaType: 'file',
        };
        insertBlockData(data?.id, payload, temp.file);
      });
      setProcessTypeLabel('Edit');
      return;
    }
    if (temp.file) {
      const payload = {
        ...temp,
        mediaPath: '',
        mediaType: 'file',
      };
      insertBlockData(getValues().id, payload, temp.file);
    }
  };

  const onEditFileFormUpload = (file) => {
    const payload = {
      id: file.id,
      data: JSON.stringify({
        id: file.id,
        displayOrder: Number(file.displayOrder),
        title: file.title,
        description: file.description,
        mediaPath: file.mediaPath,
        mediaType: 'file',
      }),
    };
    PartnerProfileService.updateBlockData(payload.id, payload.data).then(() => {
      if (file && file.file && file.file.length > 0) {
        const fileUploadPayload = {
          blockDataItemDataKey: file.dataKey,
          file: file.file,
        };
        PartnerProfileService.uploadFile(fileUploadPayload).then(() => {
          onSaveSuccessfully();
          refetch();
        });
      } else {
        onSaveSuccessfully();
        refetch();
      }
    });
  };

  const deleteFile = (id) => {
    const temp = fileList.filter((f) => f.id !== id);
    deletedFiles.push(id);
    setDeletedFiles(deletedFiles);
    setFileList(temp);
    mutationDeleteBlockData.mutate(id);
  };

  const onSubmit = (formData) => {
    const payload = {
      id: formData.id,
      contentType: 'files',
      title: formData.title,
      description: formData.description,
      displayOrder: 1,
      partnerId,
    };
    if (processTypeLabel === 'Edit') {
      mutationUpdateBlock.mutate(payload);
    } else {
      mutationSaveBlock.mutate(payload);
    }
  };

  useEffect(() => {
    if (contentBlock && contentBlock.id.length === 0) {
      setIsFetching(false);
    }
  }, []);

  return {
    isProcessing,
    handleSubmit,
    onSubmit,
    editFile,
    deleteFile,
    onFileFormCancel,
    onEditFileFormUpload,
    onAddFileFormUpload,
    onAddFile,
    deleteMutation,
    mutationUpdateBlock,
    mutationSaveBlock,
    processTypeLabel,
    control,
    onCancel,
    contentBlock,
    fileList,
    isAddFile,
    isEditFile,
    selectedFile,
    isFetching,
  };
};

export default useFileBlockEditor;
